<template>
  <!-- A recurring invoice plan's record in the app (lib/view/invoices/recurring/record/
       plan_record_screen.dart, app_en.arb): the Invoices / Recurring crumb, "Recurring invoice",
       "Edit plan" and "Send next invoice", then Billing (Amount "per month", Repeats "Monthly on
       the 1st", Next invoice, Collection "Autopay · Visa •••• 4242") and So far (Invoices,
       Collected, Failed). The plan, client and figures are sample data. Once in view the rows
       settle in turn and So far counts the latest paid cycle (about 3 s, once). Resting styles
       are the final frame. -->
  <div ref="rootEl" class="spr" :class="{ 'is-run': running || armed, 'is-armed': armed }">
    <div class="spr-window">
      <p class="spr-crumb"><span>Invoices</span> / <span>Recurring</span> / <b translate="no">Website care</b></p>
      <div class="spr-head">
        <strong translate="no">Website care</strong>
        <small>Recurring invoice · <span translate="no">Northwind Studio</span></small>
      </div>
      <div class="spr-actions"><span class="spr-btn">Edit plan</span><span class="spr-btn spr-btn--main">Send next invoice</span></div>

      <p class="spr-label">Billing</p>
      <dl class="spr-rows">
        <div class="spr-r1"><dt>Amount</dt><dd><b translate="no">$650.00</b> <small>per month</small></dd></div>
        <div class="spr-r2"><dt>Repeats</dt><dd>Monthly on the 1st</dd></div>
        <div class="spr-r3"><dt>Next invoice</dt><dd translate="no">Sun, Nov 1</dd></div>
        <div class="spr-r4"><dt>Collection</dt><dd><svg class="spr-bolt" viewBox="0 0 24 24" aria-hidden="true"><path d="M11 21h-1l1-7H7.5c-.88 0-.33-.75-.31-.78C8.48 10.94 10.42 7.54 13.01 3h1l-1 7h3.51c.4 0 .62.19.4.66C12.97 17.55 11 21 11 21z" /></svg> Autopay · <span class="spr-card" translate="no">Visa •••• 4242</span></dd></div>
      </dl>

      <p class="spr-label">So far</p>
      <div class="spr-sofar">
        <div><small>Invoices</small><b class="spr-num"><span class="spr-a" translate="no">5</span><span class="spr-b" translate="no">6</span></b></div>
        <div><small>Collected</small><b class="spr-num"><span class="spr-a" translate="no">$3,250</span><span class="spr-b" translate="no">$3,900</span></b></div>
        <div><small>Failed</small><b translate="no">0</b></div>
      </div>
    </div>
  </div>
</template>

<script setup>
const rootEl = ref(null);
const { running, armed } = useMotionInView(rootEl, { threshold: 0.4 });
</script>

<style scoped>
.spr { display: grid; place-items: center; }
.spr-window {
  width: 100%;
  max-width: 400px;
  border: 1px solid rgb(255 255 255 / 0.14);
  border-radius: 10px;
  background: #1c1c1c;
  color: #ececec;
  font-family: "Space Grotesk", Inter, ui-sans-serif, system-ui, sans-serif;
  font-size: 13px;
  line-height: 1.45;
  padding: 16px 18px 18px;
  box-shadow: 0 24px 60px -20px rgb(0 0 0 / 0.8);
}
.spr-crumb { color: #9a9a9a; font-size: 11.5px; }
.spr-crumb b { color: #ececec; font-weight: 500; }
.spr-head { display: grid; gap: 2px; margin-top: 10px; }
.spr-head strong { font-size: 20px; font-weight: 700; line-height: 1.2; }
.spr-head small { color: #a8a8a8; font-size: 12px; }
.spr-actions { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.spr-btn { border: 1px solid rgb(255 255 255 / 0.16); border-radius: 8px; padding: 6px 11px; font-size: 12.5px; font-weight: 600; }
.spr-btn--main { border-color: #ececec; background: #ececec; color: #171717; }
.spr-label { margin-top: 18px; color: #a8a8a8; font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; }
.spr-rows { margin: 6px 0 0; }
.spr-rows div { display: grid; grid-template-columns: 104px 1fr; gap: 8px; border-bottom: 1px solid rgb(255 255 255 / 0.08); padding: 7px 0; }
.spr-rows dt { color: #a8a8a8; }
.spr-rows dd { margin: 0; }
.spr-rows small { color: #a8a8a8; font-size: 12px; }
.spr-card { white-space: nowrap; }
.spr-bolt { display: inline-block; width: 13px; height: 13px; margin-right: 2px; vertical-align: -2px; fill: #a8a8a8; }
.spr-sofar { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); margin-top: 6px; border: 1px solid rgb(255 255 255 / 0.12); border-radius: 8px; background: #242424; }
.spr-sofar > div { display: grid; gap: 2px; padding: 9px 12px; }
.spr-sofar > div + div { border-left: 1px solid rgb(255 255 255 / 0.1); }
.spr-sofar small { color: #a8a8a8; font-size: 11.5px; }
.spr-sofar b { font-size: 16px; font-weight: 700; font-variant-numeric: tabular-nums; }
.spr-num { display: grid; }
.spr-num > span { grid-area: 1 / 1; }
.spr-a { opacity: 0; }

/* Resting frame: six invoices, $3,900 collected. When it plays the rows settle, then So far moves from the last cycle to this one. */
.is-run .spr-rows div { animation: spr-in 0.4s ease-out both; }
.is-run .spr-r2 { animation-delay: 0.15s; }
.is-run .spr-r3 { animation-delay: 0.3s; }
.is-run .spr-r4 { animation-delay: 0.45s; }
.is-run .spr-a { animation: spr-gone 0.3s ease-in 1.9s both; }
.is-run .spr-b { animation: spr-fade 0.3s ease-out 2.2s both; }
.is-armed * { animation-play-state: paused !important; }
@keyframes spr-in { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
@keyframes spr-fade { from { opacity: 0; } to { opacity: 1; } }
@keyframes spr-gone { from { opacity: 1; } to { opacity: 0; } }
@media (max-width: 639px) {
  .spr-window { padding: 14px; }
  .spr-rows div { grid-template-columns: 92px 1fr; }
  .spr-sofar > div { padding: 8px 9px; }
  .spr-sofar b { font-size: 14px; }
}
@media (prefers-reduced-motion: reduce) { .spr * { animation: none !important; } }
</style>
